import { Module } from 'vuex'
import { actions } from './actions'
import { RootState } from '@/store'
import { PaymentDocument } from '@/views/App/Deal/store/interfaces'
import {
  Deal, Document, Owners, OwnershipTypesResponse, OwnersTypeEnum, PaymentTypesResponse,
  DocumentTypeEnum, SmallAttachmentTypeEnum
} from '@/requests/mainApi'

export const storeModuleName:string = 'dealStore'

export interface DealState {
  ownerships: OwnershipTypesResponse[]
  owners: Owners[]
  ownerDocumentsList: Document[]
  paymentDetails: PaymentTypesResponse
  dealDocuments: Document[]
  contract: Blob
}

const state: DealState = {
  ownerships: [],
  owners: [],
  ownerDocumentsList: [],
  paymentDetails: { basic: [], additional: [], banks: [] },
  dealDocuments: [],
  contract: new Blob()
}

export const emptyOwner = {
  firstName: '',
  lastName: '',
  patronymicName: '',
  // @ts-ignore
  birthday: null,
  type: '' as OwnersTypeEnum
}

export const store: Module<DealState, RootState> = {
  state,
  getters: {
    ownerships (state) {
      return state.ownerships
    },
    currentOwnership (state, getters, rootState, rootGetters): OwnershipTypesResponse | undefined {
      return state.ownerships
        .find((item:OwnershipTypesResponse) => item.type === rootGetters.deal.typeOfProperty)
    },
    owners (state) {
      return state.owners
    },
    ownerDocumentsList (state) {
      return state.ownerDocumentsList
    },
    paymentDetails (state) {
      return state.paymentDetails
    },
    dealDocuments (state) {
      return state.dealDocuments
    },
    paymentDocuments (state) {
      return state.dealDocuments.filter(doc => doc && !doc.owner)
    },
    contract (state) {
      return state.contract
    },
    neededPaymentDocuments (state, getters, rootState, rootGetters): PaymentDocument[] {
      // Для типов документов (DocumentTypeEnum),
      // которые требуют более 1 attachment.
      const getDocsList = (doc: string) => {
        switch (doc) {
          case 'maternal_capital_certificate': return ['maternal_capital_certificate', 'maternal_capital_certificate_balance']
          default: return [doc]
        }
      }
      const docTypes = [
        ...state.paymentDetails.basic
          .find(doc => doc.type === rootGetters.deal.basePaymentType)?.documents || [],
        ...state.paymentDetails.additional
          .find(doc => doc.type === rootGetters.deal.additionalPaymentType)?.documents || []
      ]
      return docTypes
        .map(documentType =>
          getDocsList(documentType)
            .map(documentName => {
              return {
                type: documentType as DocumentTypeEnum,
                name: documentName as SmallAttachmentTypeEnum
              }
            }))
        .flat()
    },
    isEnoughDocuments (state, getters) {
      const paymentDocuments = getters.paymentDocuments.map((docs: Document) => docs.attachments).flat()
      return Boolean(paymentDocuments.length >= getters.neededPaymentDocuments.length)
    }
  },
  mutations: {
    setOwnerships (state, value) {
      state.ownerships = value
    },
    setOwners (state, value) {
      state.owners = value
    },
    addOwner (state, value) {
      state.owners = [...state.owners, value]
    },
    removeOwner (state, value) {
      state.owners.splice(value, 1)
    },
    setOwnerDocumentsList (state, value) {
      state.ownerDocumentsList = value
    },
    setPaymentDetails (state, value) {
      state.paymentDetails = value
    },
    setDealDocuments (state, value) {
      state.dealDocuments = value
    },
    setContract (state, value) {
      state.contract = value
    },
    removePaymentDocFromList (state, value) {
      state.dealDocuments = state.dealDocuments.filter(doc => doc.id !== value.id)
    }
  },
  actions,
  namespaced: true
}
